import { PublicPageShell } from '@/components/PublicPageShell';
import type { Lang } from './LandingPage';

interface TermsPageProps {
  lang: Lang;
  setLang: (lang: Lang) => void;
  onBack: () => void;
}

const content = {
  zh: {
    title: '服务条款',
    intro: '欢迎使用 TingYue（以下简称“本服务”）。请在使用前仔细阅读以下条款。',
    sections: [
      {
        title: '1. 服务说明',
        text: 'TingYue 是一款面向个人投资者的自托管量化交易助手，提供自选股跟踪、交易记录、组合管理、AI 分析、价格预警等功能。本服务默认以自托管方式部署，数据存储在用户自己的服务器或设备上。',
      },
      {
        title: '2. 账号与密码',
        text: '用户注册时需提供真实、有效的邮箱地址，并妥善保管密码。因用户泄露密码导致的损失由用户自行承担。',
      },
      {
        title: '3. 数据与隐私',
        text: 'TingYue 默认将用户数据保存在本地 SQLite 数据库中，不会主动上传到我们的服务器。用户自行配置的大模型 API Key 会经服务端加密存储，我们不会读取或用于其他目的。',
      },
      {
        title: '4. 第三方服务',
        text: 'AI 分析功能依赖用户自行提供的大模型 API 服务。行情数据来自公开网络接口，我们不对第三方数据的实时性、准确性做任何担保。',
      },
      {
        title: '5. 风险提示',
        text: 'TingYue 提供的分析、建议和数据仅供参考，不构成任何投资建议。用户应独立判断并承担全部交易风险。',
      },
      {
        title: '6. 责任限制',
        text: '在法律允许的最大范围内，TingYue 及其开发者不对因使用或无法使用本服务而导致的任何直接、间接或附带损失承担责任。',
      },
      {
        title: '7. 条款更新',
        text: '我们保留随时更新本服务条款的权利。更新后的条款将在网站上公布，用户继续使用即视为接受。',
      },
    ],
    contact: '如有任何问题，请通过网站底部备案链接所在页面或 GitHub Issues 联系我们。',
  },
  en: {
    title: 'Terms of Service',
    intro: 'Welcome to TingYue (the “Service”). Please read these terms carefully before using the Service.',
    sections: [
      {
        title: '1. Service Description',
        text: 'TingYue is a self-hosted quantitative trading assistant for individual investors, providing watchlist tracking, trade records, portfolio management, AI analysis, price alerts and more. The Service is deployed by default in a self-hosted manner, with data stored on the user\'s own server or device.',
      },
      {
        title: '2. Account and Password',
        text: 'When registering, users must provide a valid email address and keep their password secure. Users are solely responsible for any losses caused by password leakage.',
      },
      {
        title: '3. Data and Privacy',
        text: 'TingYue stores user data in a local SQLite database by default and does not upload it to our servers. LLM API keys configured by users are encrypted at rest on the server; we do not read or use them for other purposes.',
      },
      {
        title: '4. Third-Party Services',
        text: 'AI analysis relies on LLM API services provided by the user. Market data is sourced from public network interfaces, and we make no guarantees regarding the timeliness or accuracy of third-party data.',
      },
      {
        title: '5. Risk Disclosure',
        text: 'Any analysis, suggestions or data provided by TingYue are for reference only and do not constitute investment advice. Users should make independent judgments and bear all trading risks.',
      },
      {
        title: '6. Limitation of Liability',
        text: 'To the maximum extent permitted by law, TingYue and its developers shall not be liable for any direct, indirect or incidental damages arising from the use or inability to use the Service.',
      },
      {
        title: '7. Updates to Terms',
        text: 'We reserve the right to update these terms at any time. Updated terms will be posted on the website, and continued use constitutes acceptance.',
      },
    ],
    contact: 'If you have any questions, please contact us via the GitHub Issues page linked from the site footer.',
  },
};

export function TermsPage({ lang, setLang, onBack }: TermsPageProps) {
  const c = content[lang];
  return (
    <PublicPageShell title={c.title} lang={lang} setLang={setLang} onBack={onBack}>
      <p className="text-muted-foreground mb-8">{c.intro}</p>
      {c.sections.map((s, i) => (
        <div key={i} className="mb-6">
          <h2 className="text-xl font-semibold mb-2">{s.title}</h2>
          <p className="text-muted-foreground leading-relaxed">{s.text}</p>
        </div>
      ))}
      <p className="text-sm text-muted-foreground mt-8">{c.contact}</p>
    </PublicPageShell>
  );
}
